// manualServices.js — RTK Query hooks for the in-portal user manual (V081).
//
// Levi, 2026-10-01 ("Manual page for portal"): one page that documents EVERY
// portal page and feature from a user's point of view, searchable, and served
// from the same table the Kai chat answers from — so the page and the bot can
// never tell a user two different things.
//
// Endpoints hit /portal-manual/portal/... (mounted in Portal-BFF
// portalManualRoute.js). The path is NOT /manual — that prefix is already taken
// by the unrelated per-employee manual-annotations feature.
//
// The whole corpus comes back in one response (a few hundred short rows) and the
// page filters it in memory. That is deliberate: it makes typing in the search
// box instant with no request per keystroke, and lets one query match any
// combination of words across title / summary / keywords / where-to-find / area.

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const manualApi = createApi({
  reducerPath: "manualApi",
  tagTypes: ["ManualEntries", "ManualAreas"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/portal-manual/portal`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Every published entry, alphabetical. Includes entries the caller's
    // position cannot open — they come back flagged { accessible: false } so the
    // page can still show that the feature exists and who to ask.
    getManualEntries: builder.query({
      query: () => ({ url: `/entries` }),
      providesTags: ["ManualEntries"],
    }),

    // One entry by slug — the target of a chat deep link (?s=<slug>).
    getManualEntry: builder.query({
      query: (slug) => ({ url: `/entry/${slug}` }),
      providesTags: ["ManualEntries"],
    }),

    // Area names + counts, for the filter chips.
    getManualAreas: builder.query({
      query: () => ({ url: `/areas` }),
      providesTags: ["ManualAreas"],
    }),
  }),
});

export const {
  useGetManualEntriesQuery,
  useGetManualEntryQuery,
  useGetManualAreasQuery,
} = manualApi;
